import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GoesOutSentence } from '../settings/AgentControls';
import { AGENTS_PATH } from '../settings/goesOut';

/**
 * Today, under its head (Ask-first defaults, backlog d51a4c40): the card a new
 * owner sees once after connecting an agent in signup, and the line that always
 * says what skips a check in tinycms. The card's flag is the member's own, on
 * the server, so it shows on another device and stays gone once closed.
 */

const FIRST_RUN_AGENT_CARD = gql`
  query FirstRunAgentCard($accountId: ID!) {
    firstRunAgentCard(accountId: $accountId)
  }
`;

const DISMISS_FIRST_RUN_AGENT_CARD = gql`
  mutation DismissFirstRunAgentCard($accountId: ID!) {
    dismissFirstRunAgentCard(accountId: $accountId)
  }
`;

const COLUMNS: Array<[string, string, string]> = [
  [
    'Now',
    'It writes drafts',
    'Articles, pages and posts arrive as drafts. Readers see none of it.',
  ],
  [
    'Each time',
    'You approve what goes out',
    'Publishing, changes to the site and deleting wait here on Today until you approve them.',
  ],
  [
    'When you’re ready',
    'Loosen one action at a time',
    'In Approvals, switch an action to Goes out without asking. Switch it back whenever you like.',
  ],
];

function FirstRunCard({ accountId }: { accountId: string }) {
  const navigate = useNavigate();
  const { data } = useQuery<{ firstRunAgentCard: string | null }>(FIRST_RUN_AGENT_CARD, {
    variables: { accountId },
    fetchPolicy: 'cache-and-network',
  });
  const [dismiss] = useMutation(DISMISS_FIRST_RUN_AGENT_CARD);
  const [closed, setClosed] = useState(false);
  // The site changed under this page: its own card, if it has one.
  useEffect(() => setClosed(false), [accountId]);
  const agent = data?.firstRunAgentCard;
  if (!agent || closed) return null;

  const close = () => {
    setClosed(true);
    void dismiss({
      variables: { accountId },
      refetchQueries: [FIRST_RUN_AGENT_CARD],
    }).catch(() => {});
  };

  return (
    <section className="tdc-card" aria-label={`${agent} starts out asking you first`}>
      <button type="button" className="tdc-close" aria-label="Close" onClick={close}>
        ✕
      </button>
      <h2 className="tdc-title">{agent} starts out asking you first</h2>
      <p className="tdc-sub">
        You decide how much it does on its own. For now, that’s writing drafts.
      </p>
      <div className="tdc-cols">
        {COLUMNS.map(([eyebrow, head, body]) => (
          <div className="tdc-col" key={eyebrow}>
            <div className="tdc-eyebrow">{eyebrow}</div>
            <div className="tdc-col-head">{head}</div>
            <p className="tdc-col-body">{body}</p>
          </div>
        ))}
      </div>
      <div className="tdc-foot">
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          onClick={() => {
            close();
            navigate(AGENTS_PATH);
          }}
        >
          See what it can do
        </button>
        <button type="button" className="ink-quiet-link" onClick={close}>
          Got it
        </button>
        <span className="tdc-foot-note">
          Every approval and every change to these is kept in the Activity Log.
        </span>
      </div>
    </section>
  );
}

export function TodayAgentControls({ accountId }: { accountId?: string | null }) {
  if (!accountId) return null;
  return (
    <div className="tdc" data-enter={0}>
      <FirstRunCard accountId={accountId} />
      <GoesOutSentence accountId={accountId} />
    </div>
  );
}
